import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';

import {connect} from 'react-redux';
import {compose} from 'redux';
import {injectIntl, intlShape, defineMessages} from 'react-intl';

import VM from 'openblock-vm';
import analytics from '../lib/analytics';
import {closeCompileProgress} from '../reducers/modals';
import {showAlertWithTimeout} from '../reducers/alerts';

import CompileProgressComponent, {PHASES} from '../components/compile-progress/compile-progress.jsx';

const messages = defineMessages({
    compileErrorMessage: {
        defaultMessage: 'Compile error',
        description: 'Prompt for compile error',
        id: 'gui.compileProgress.compileErrorMessage'
    },
    compileTimeout: {
        defaultMessage: 'Compile timeout',
        description: 'Prompt for compile timeout',
        id: 'gui.compileProgress.compileTimeoutMessage'
    }
});

// 60s
const COMPILE_TIMEOUT_TIME = 60 * 1000;

class CompileProgress extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleCancel',
            'handleHelp',
            'handleStdout',
            'handleCompileError',
            'handleCompileSuccess',
            'handleCompileTimeout'
        ]);
        this.state = {
            extension: this.props.deviceData.find(dev => dev.deviceId === props.deviceId),
            phase: PHASES.compiling,
            peripheralName: null,
            text: ''
        };
        // if the compile progress stacks some seconds without any info,
        // set state to timeout so user can close the modal.
        this.compileTimeout = setTimeout(() => this.handleCompileTimeout(), COMPILE_TIMEOUT_TIME);
        analytics.event({
            category: 'devices',
            action: 'compiling',
            label: this.props.deviceId
        });
    }
    componentDidMount () {
        this.props.vm.on('PERIPHERAL_COMPILE_STDOUT', this.handleStdout);
        this.props.vm.on('PERIPHERAL_COMPILE_ERROR', this.handleCompileError);
        this.props.vm.on('PERIPHERAL_CONNECTION_LOST_ERROR', this.handleCompileError);
        this.props.vm.on('PERIPHERAL_COMPILE_SUCCESS', this.handleCompileSuccess);
    }
    componentWillUnmount () {
        this.props.vm.removeListener('PERIPHERAL_COMPILE_STDOUT', this.handleStdout);
        this.props.vm.removeListener('PERIPHERAL_COMPILE_ERROR', this.handleCompileError);
        this.props.vm.removeListener('PERIPHERAL_CONNECTION_LOST_ERROR', this.handleCompileError);
        this.props.vm.removeListener('PERIPHERAL_COMPILE_SUCCESS', this.handleCompileSuccess);
        clearTimeout(this.compileTimeout);
    }
    handleCancel () {
        this.props.onCloseCompileProgress();
    }
    handleHelp () {
        window.open(this.state.extension.helpLink, '_blank');
        analytics.event({
            category: 'devices',
            action: 'compile help',
            label: this.props.deviceId
        });
    }
    handleStdout (data) {
        this.setState({
            text: this.state.text + data.message
        });
        clearTimeout(this.compileTimeout);
        this.compileTimeout = setTimeout(() => this.handleCompileTimeout(), COMPILE_TIMEOUT_TIME);
    }
    handleCompileError (data) {
        // if the compile progress has been in success don't handle the compile error.
        if (this.state.phase !== PHASES.success) {
            this.setState({
                text: `${this.state.text + data.message}\r\n` +
                    `${this.props.intl.formatMessage(messages.compileErrorMessage)}`,
                phase: PHASES.error
            });
            this.props.onCompileError();
            analytics.event({
                category: 'devices',
                action: 'compile error',
                label: this.props.deviceId
            });
            clearTimeout(this.compileTimeout);
        }
    }
    handleCompileSuccess () {
        this.setState({
            phase: PHASES.success
        });
        this.props.onCompileSuccess();
        clearTimeout(this.compileTimeout);
    }
    handleCompileTimeout () {
        this.setState({
            text: `${this.state.text}\r\n${this.props.intl.formatMessage(messages.compileTimeout)}`,
            phase: PHASES.timeout
        });
        this.props.onCompileError();
        analytics.event({
            category: 'devices',
            action: 'compile timeout',
            label: this.props.deviceId
        });
        clearTimeout(this.compileTimeout);
    }

    render () {
        return (
            <CompileProgressComponent
                connectionSmallIconURL={this.state.extension && this.state.extension.connectionSmallIconURL}
                name={this.state.extension && this.state.extension.name}
                onCancel={this.handleCancel}
                onHelp={this.handleHelp}
                text={this.state.text}
                phase={this.state.phase}
            />
        );
    }
}

CompileProgress.propTypes = {
    deviceData: PropTypes.instanceOf(Array).isRequired,
    deviceId: PropTypes.string.isRequired,
    intl: intlShape.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired,
    onCloseCompileProgress: PropTypes.func.isRequired,
    onCompileError: PropTypes.func.isRequired,
    onCompileSuccess: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    deviceData: state.scratchGui.deviceData.deviceData,
    deviceId: state.scratchGui.device.deviceId
});

const mapDispatchToProps = dispatch => ({
    onCloseCompileProgress: () => dispatch(closeCompileProgress()),
    onCompileError: () => showAlertWithTimeout(dispatch, 'uploadError'),
    onCompileSuccess: () => showAlertWithTimeout(dispatch, 'uploadSuccess')
});

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(CompileProgress);
